import { DatePipe, DecimalPipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  Router,
  RouterModule,
  RouterOutlet,
  withRouterConfig
} from '@angular/router';
import * as Sentry from '@sentry/angular';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { AgdPreset } from '../agd-preset-theme';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from 'src/environments/environment';
import {
  initializeEnvironmentConfig,
  preloadLanguage
} from './app-initializer';
import { routes } from './app.routes';
import { LANGS } from './constants/lang.const';
import { TraceabilityInterceptor } from './interceptors/traceability.interceptor';
import { PrimengExportsModule } from './primeng-exports.module';
import { TranslocoHttpLoader } from './services/transloco-http-loader.service';
import { UserConfigService } from './services/user-config.service';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      RouterModule,
      RouterOutlet,
      BrowserModule,
      PrimengExportsModule
    ),
    DatePipe,
    DecimalPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TraceabilityInterceptor,
      multi: true
    },
    MessageService,
    ConfirmationService,
    // provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
        onSameUrlNavigation: 'reload'
      })
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: AgdPreset,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng'
          },
          darkModeSelector: 'none'
        }
      }
    }),
    provideTransloco({
      config: {
        availableLangs: [...LANGS],
        defaultLang: 'engb',
        fallbackLang: 'engb',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: environment.production
      },
      loader: TranslocoHttpLoader
    }),
    provideAppInitializer(() => {
      const translocoService = inject(TranslocoService);
      return preloadLanguage(translocoService);
    }),
    provideAppInitializer(() => {
      const userConfigService = inject(UserConfigService);
      return initializeEnvironmentConfig(userConfigService);
    }),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    })
  ]
};
